import { useEffect, useMemo, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import purchaseOrdersAPI from '../../api/po';
import { useAuth } from '../../context/AuthContext';
import { trackEvent } from '../../services/telemetry';
import {
  buildOverduePurchaseOrderActions,
  reconcileOverdueAlertLifecycle,
} from '../../pages/operations/commandCenterLifecycle';

const REFRESH_INTERVAL_MS = 60_000;

export default function CommandCenterLifecycleTracker() {
  const { user } = useAuth();
  const shownOverdueAlertsRef = useRef(new Set());
  const seenOverdueAlertsRef = useRef(new Map());
  const trackedOrganizationRef = useRef(null);
  const organizationId = localStorage.getItem('currentOrganization')
    || user?.organizationId
    || user?.organization?._id
    || null;

  const { data: poData } = useQuery({
    queryKey: ['cmd-po-pipeline'],
    queryFn: async () => {
      const response = await purchaseOrdersAPI.getAll({
        status: 'draft,pending,approved,ordered',
        limit: 100,
      });
      return response.data;
    },
    staleTime: REFRESH_INTERVAL_MS,
    refetchInterval: REFRESH_INTERVAL_MS,
    refetchOnMount: 'always',
    retry: 1,
  });

  const poList = useMemo(
    () => poData?.purchaseOrders ?? poData?.data ?? [],
    [poData]
  );

  useEffect(() => {
    if (trackedOrganizationRef.current !== organizationId) {
      shownOverdueAlertsRef.current.clear();
      seenOverdueAlertsRef.current.clear();
      trackedOrganizationRef.current = organizationId;
    }

    const doNow = buildOverduePurchaseOrderActions({
      poList,
      organizationId,
      now: new Date(),
    });

    reconcileOverdueAlertLifecycle({
      doNow,
      shownAlerts: shownOverdueAlertsRef.current,
      seenAlerts: seenOverdueAlertsRef.current,
      track: trackEvent,
      now: new Date(),
      readOrgId: () => organizationId,
    });
  }, [organizationId, poList]);

  return null;
}
